import { useClerk } from '@clerk/react';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authQueryKeys } from '../hooks/auth/use-memberships';
import { consultantAccessNoticeRequestedAtAtom } from '../stores/consultant-access-notice.store';
import { ContactSupportButton } from './contact-support-button';

/**
 * Full-screen state for a Consulente blocked on every company: there is no
 * company to open, so the only useful moves are contacting support or signing out.
 * It says everything the app-level notice dialog would: the dialog never opens
 * while this screen applies, and the notice request it answered is cleared here
 * so it cannot pop up later, once access comes back.
 */
export function ConsultantProfileInactiveScreen() {
  const { t } = useTranslation();
  const { signOut } = useClerk();
  const queryClient = useQueryClient();
  const setNoticeRequestedAt = useSetAtom(consultantAccessNoticeRequestedAtAtom);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    setNoticeRequestedAt(null);
  }, [setNoticeRequestedAt]);

  async function handleRetry() {
    setIsRetrying(true);
    try {
      await queryClient.invalidateQueries({ queryKey: authQueryKeys.memberships() });
    } finally {
      setIsRetrying(false);
    }
  }

  return (
    <Box
      component="main"
      sx={{
        position: 'fixed',
        zIndex: 9999,
        inset: 0,
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
      }}
    >
      <LockPersonOutlinedIcon sx={{ fontSize: 64, color: 'text.primary' }} aria-hidden />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, textAlign: 'center' }}>
        {t('consultant.notActivePartner.title')}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 420 }}
      >
        {t('consultant.notActivePartner.body')}
      </Typography>
      <Stack spacing={1} sx={{ width: '100%', maxWidth: 320, mt: 1 }}>
        <ContactSupportButton />
        <Button
          variant="outlined"
          onClick={handleRetry}
          disabled={isRetrying}
          startIcon={<RefreshOutlinedIcon />}
          sx={{ minHeight: 48 }}
        >
          {t('consultant.notActivePartner.retry')}
        </Button>
        <Button
          variant="outlined"
          onClick={() => signOut({ redirectUrl: '/' })}
          sx={{ minHeight: 48 }}
        >
          {t('consultant.notActivePartner.signOut')}
        </Button>
      </Stack>
    </Box>
  );
}
